/* eslint-disable no-console */

/**
 * @module  public/js/services/analyticsservice.js
 * @class   AnalyticsService
 * @summary Fault-tolerant wrapper for logging user events to Firestore.
 *
 * @description
 * Writes `{ event, uid, payload, ts }` docs to a Firestore collection
 * (default **AuditLogs**).  Payloads can opt into Firestore TTL by passing a
 * valid `Date` at `payload.expiresAt`, which is promoted to a top-level
 * document field. All dependencies can be overridden for
 * unit-testing; errors are swallowed in production to avoid UX impact.
 *
 * ## Public API
 * – `track(eventName, payload?)` — persist an analytic event
 *
 * @author    Vision & Story
 * @version   1.1.0
 * @since     2025-04-29
 */

import { serverTimestamp } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { auth as fbAuth, db as fbDb } from '../core/firebase-service.js';
import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';

export default class AnalyticsService {
  /** @type {import('firebase/auth').Auth}   */ #auth;
  /** @type {import('firebase/firestore').Firestore} */ #db;
  #collectionName;

  /**
   * @param {object}  [deps]
   * @param {import('./authservice.js').default} [deps.authService] – DI for tests
   * @param {import('firebase/firestore').Firestore} [deps.db]      – DI for tests
   * @param {string} [deps.collectionName='AuditLogs']             – override collection
   */
  constructor({
    authService = null,
    db = null,
    collectionName = 'AuditLogs',
  } = {}) {
    this.#auth = authService ?? fbAuth;
    this.#db = db ?? fbDb;
    this.#collectionName = collectionName;
  }

  /**
   * Persist an analytics event.
   *
   * @param  {string}                eventName – e.g. "Home-ViewChange"
   * @param  {Record<string, any>?=} payload   – optional contextual data
   * @return {Promise<void>}
   * @throws {TypeError} If `eventName` is falsy
   */
  async track(eventName, payload = null) {
    if (!eventName)
      throw new TypeError('AnalyticsService.track: eventName required');

    try {
      let uid = 'anonymous';
      let tenantId;
      let user = null;

      if (this.#auth) {
        if (typeof this.#auth.requireAuth === 'function') {
          user = await this.#auth.requireAuth().catch(() => null);
        } else {
          user = this.#auth.currentUser ?? null;
        }
        if (user) {
          uid = user.uid;
          tenantId = user.tenantId;
          if (!tenantId) {
            console.error('Missing tenant ID');
            return;
          }
        }
      }

      if (!tenantId) return;

      const ref = docWithTenant(
        tenantId,
        `${this.#collectionName}/${crypto.randomUUID()}`
      );

      const hasObjectPayload =
        payload !== null &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        !(payload instanceof Date);
      const analyticsPayload = hasObjectPayload ? { ...payload } : payload;
      const payloadExpiry =
        hasObjectPayload &&
        analyticsPayload.expiresAt instanceof Date &&
        Number.isFinite(analyticsPayload.expiresAt.getTime())
          ? analyticsPayload.expiresAt
          : null;
      if (payloadExpiry) {
        delete analyticsPayload.expiresAt;
      }

      const logEntry = {
        event: eventName,
        uid,
        payload: analyticsPayload,
        ts: serverTimestamp(),
      };
      if (payloadExpiry) {
        logEntry.expiresAt = payloadExpiry;
      }

      await writeWithTenant(tenantId, ref, logEntry);
    } catch (err) {
      /* istanbul ignore next – non-critical path */
      const runtimeProcess = globalThis.process;
      const shouldLogTrackFailure =
        runtimeProcess && typeof runtimeProcess === 'object'
          ? runtimeProcess.env?.NODE_ENV !== 'production'
          : ['localhost', '127.0.0.1'].includes(
              globalThis.location?.hostname || ''
            );
      if (shouldLogTrackFailure) {
        console.error('[AnalyticsService] track() failed:', err);
      }
      // In production we deliberately swallow to keep UI responsive.
    }
  }
}
